import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { ContextService } from 'src/core/services/context.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { AgentFormView } from './agent-form.view';
import { AgentDataService } from './agent-form.data';
import { AgentBrandDataService } from 'src/app/bookings/agent-brands/agent-brand-form.data';
import { AgentPolicyDataService } from 'src/app/bookings/agent-policies/agent-policy-form.data';
import { CurrencyListDataService } from 'src/app/general/currencies/list/currency-list.data';
import { IdentityService } from 'src/core/services/identity.service';
import { AgentMarketDataService } from 'src/app/bookings/agent-markets/agent-market-form.data';

interface Props extends ControllerProps<{ id: string; }> {
}

interface State {
	mode: ControlMode;
	currency: CurrencySummary;
}

@observer
export class AgentFormController extends React.Component<Props, State> {
	@resolve
	private contextService: ContextService;

	@resolve
	private identity: IdentityService;

	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: AgentDataService;

	@observable
	private brandDataService: AgentBrandDataService;

	@observable
	private policyDataService: AgentPolicyDataService;

	@observable
	private marketDataService: AgentMarketDataService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			mode: 'display',
			currency: null
		};
	}

	componentDidMount(): void {
		this.initialize(this.props.match.params.id);
	}

	componentWillReceiveProps(nextProps: Readonly<Props>): void {
		if (nextProps.match.params.id !== this.props.match.params.id) {
			this.initialize(nextProps.match.params.id);
		}
	}

	@computed
	get title(): string {
		if (this.dataService && this.dataService.item) {
			return `Agent ${this.dataService.item.name || ''}`;
		}

		return '';
	}

	private isNew(): boolean {
		return this.props.match.params.id === 'new';
	}

	private initialize(id: string): void {
		this.props.setBusy(true);
		this.loadData(id)
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	private async loadCurrency(currencyId: number): Promise<void> {
		const currencyListDataService = CurrencyListDataService.make(this.contextService);
		currencyListDataService.addFilter('id', 'eq', currencyId);
		currencyListDataService.addSorting('id', 'asc', true);
		currencyListDataService.pageSize = 1;

		await currencyListDataService.load(query => query);
		this.setState({
			currency: currencyListDataService.item
		});
	}

	private async loadRelationship<T extends AgentRelation>(relationshipDataService: DataSet<T>, agentId: number, sortBy: keyof T = 'id'): Promise<void> {
		relationshipDataService.addFilter('agentId', 'eq', agentId);
		relationshipDataService.addSorting(sortBy, 'asc', true);
		relationshipDataService.pageSize = 20;

		await relationshipDataService.load(query => query);
	}

	private async loadData(id: string): Promise<void> {
		this.dataService = AgentDataService.make(this.contextService);
		this.brandDataService = AgentBrandDataService.make(this.contextService);
		this.policyDataService = AgentPolicyDataService.make(this.contextService);
		this.marketDataService = AgentMarketDataService.make(this.contextService)

		if (id === 'new') {
			// Create new
			await this.dataService.add(this.identity.userId);
			this.handleModeChange('edit');
		} else {
			// Load entity
			const agentId = parseInt(id);
			this.dataService.addFilter('id', 'eq', agentId);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 1;

			this.loadRelationship(this.brandDataService, agentId);
			this.loadRelationship(this.policyDataService, agentId, 'priority');
			this.loadRelationship(this.marketDataService, agentId);
			await this.dataService.load(query => query);
			const agent = this.dataService.item;
			if (agent.dynamicProperties.isATOL == undefined)
				agent.dynamicProperties.isATOL = false;
			this.loadCurrency(agent.currencyId);
		}
	}

	handleModeChange = (mode: ControlMode): void => {
		this.setState({ mode });
	};

	handleChangeCurrency = (currency: CurrencySummary): void => {
		this.setState({ currency });
	};

	save = async (): Promise<void> => {
		const item = this.dataService.item;
		const errors = await item.validate(true);
		if (errors.length > 0) {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
			return;
		}

		this.props.setBusy(true);
		try {
			item.brands = undefined;
			item.policies = undefined;
			item.markets = undefined;
			await this.dataService.save();
			this.toast.showDataSaved();
			this.props.history.replace(`/bookings/agents/${item.entity.id}`);
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		}

		this.props.setBusy(false);
	};

	cancel = (): void => {
		this.props.history.push('/bookings/agents');
	};

	render(): React.ReactNode {
		if (this.dataService == null || this.dataService.item == null) {
			return null;
		}

		return (
			<AgentFormView
				offset={false}
				model={this.dataService.item}
				brandDataService={this.brandDataService}
				policyDataService={this.policyDataService}
				marketDataService={this.marketDataService}
				title={this.title}
				isNew={this.isNew()}
				cancel={this.cancel}
				save={this.save}
				contextService={this.contextService}
				mode={this.state.mode}
				onModeChange={this.handleModeChange}
				currency={this.state.currency}
				onChangeCurrency={this.handleChangeCurrency}
				{...this.props}
			/>
		);
	}
}

interface AgentRelation { id: number; agentId?: number; }



// WEBPACK FOOTER //
// ./src/app/bookings/agents/form/agent-form.controller.tsx